import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Check } from "lucide-react";
import { GB, DE, IT, ES, FR, PT, AL } from "country-flag-icons/react/3x2";
import { LANGS, LANG_LABELS, href } from "../content/shared";
import { useT } from "../i18n";
import { Logo } from "./Brand";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";

const NAV = [["product", "product"], ["how", "how"], ["useCases", "useCases"], ["pricing", "pricing"], ["help", "help"]];

export const swapLang = (pathname, to) => {
  const parts = pathname.split("/");
  parts[1] = to;
  return parts.join("/") || `/${to}/`;
};

const FLAGS = { en: GB, de: DE, it: IT, es: ES, fr: FR, pt: PT, sq: AL };
export const Flag = ({ lang, className = "h-3.5 w-[21px]" }) => {
  const F = FLAGS[lang];
  return <F className={`shrink-0 rounded-[2px] ring-1 ring-black/10 ${className}`} aria-hidden data-testid={`flag-${lang}`} />;
};

// Remembers a manual language choice so the root URL opens it next time.
const remember = (l) => { try { localStorage.setItem("cp-lang", l); } catch (e) { /* storage blocked */ } };

const LangSwitch = () => {
  const { lang, t } = useT();
  const { pathname } = useLocation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="header-lang-trigger" aria-label={t.nav.language} className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-surface px-3 text-sm font-medium uppercase hover:border-foreground/40">
        <Flag lang={lang} /> {lang}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {LANGS.map((l) => (
          <DropdownMenuItem key={l} asChild>
            <Link to={swapLang(pathname, l)} onClick={() => remember(l)} hrefLang={l} lang={l} data-testid={`header-lang-${l}`} className={`flex cursor-pointer items-center gap-3 ${l === lang ? "text-primary" : ""}`}>
              <Flag lang={l} className="h-4 w-6" /><span className="flex-1 font-medium">{LANG_LABELS[l]}</span>{l === lang && <Check className="h-4 w-4" aria-hidden />}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function Header() {
  const { lang, t } = useT();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  const navCls = ({ isActive }) => `rounded-full px-3 py-2 text-sm font-medium transition-colors ${isActive ? "text-foreground bg-surface-2" : "text-muted-foreground hover:text-foreground"}`;

  return (
    <header className={`sticky top-0 z-50 border-b transition-colors ${scrolled || open ? "border-border bg-background/95" : "border-transparent bg-background"}`}>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:rounded focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground">{t.nav.skip}</a>
      <div className="container-x flex h-16 items-center justify-between gap-4 md:h-[4.5rem]">
        <Link to={href(lang, "home")} aria-label="ChatPlate" data-testid="header-logo-link"><Logo /></Link>
        <nav aria-label="Main" className="hidden items-center gap-1 lg:flex">
          {NAV.map(([key, label]) => (
            <NavLink key={key} to={href(lang, key)} className={navCls} data-testid={`nav-${key}`}>{t.nav[label]}</NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-2 lg:flex">
          <LangSwitch />
          <Link to={href(lang, "download")} className="btn-primary h-10" data-testid="header-download-btn">{t.nav.download}</Link>
        </div>
        <button type="button" className="grid h-10 w-10 place-items-center rounded-full border border-border lg:hidden" aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? t.nav.close : t.nav.menu} onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div id="mobile-menu" className="h-[calc(100dvh-4rem)] overflow-y-auto border-t border-border bg-background lg:hidden" data-testid="mobile-menu">
          <nav aria-label="Mobile" className="container-x flex flex-col py-6">
            {[["home", "home"], ...NAV, ["safety", "safety"], ["about", "about"], ["support", "support"]].map(([key, label]) => (
              <NavLink key={key} end to={href(lang, key)} data-testid={`mobile-nav-${key}`} className="border-b border-border py-4 font-display text-2xl font-semibold">{t.nav[label]}</NavLink>
            ))}
            <div className="mt-8 flex flex-wrap gap-2" role="group" aria-label={t.nav.language}>
              {LANGS.map((l) => (
                <Link key={l} to={swapLang(pathname, l)} onClick={() => remember(l)} hrefLang={l} data-testid={`mobile-lang-${l}`} aria-current={l === lang ? "true" : undefined} className={`inline-flex h-10 items-center gap-2 rounded-full border px-3 text-sm font-medium uppercase ${l === lang ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}><Flag lang={l} />{l}</Link>
              ))}
            </div>
            <Link to={href(lang, "download")} className="btn-primary mt-8 w-full" data-testid="mobile-download-btn">{t.nav.download}</Link>
          </nav>
        </div>
      )}
    </header>
  );
}
